/**
 * `useVault` — TanStack Query hook wrapping the Vault snapshot
 * endpoint. Abstraction layer so components never import the API
 * client directly.
 *
 * Seed / reset mutations fire a localised success toast on resolve.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { vault } from '@/api/endpoints/vault';
import { useToast } from '@/components/Toaster';

export const vaultKeys = {
  snapshot: ['vault', 'snapshot'] as const,
  detail: (id: string) => ['vault', 'detail', id] as const,
};

export function useVaultSnapshot() {
  return useQuery({
    queryKey: vaultKeys.snapshot,
    queryFn: vault.snapshot,
    staleTime: 15_000,
  });
}

export function useGiDetail(id: string | undefined) {
  return useQuery({
    queryKey: vaultKeys.detail(id ?? ''),
    queryFn: () => (id ? vault.detail(id) : Promise.resolve(null)),
    enabled: Boolean(id),
  });
}

export function useSeedDemoData() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: vault.seedDemo,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultKeys.snapshot });
      toast.success(t('toast.created'));
    },
  });
}

export function useResetVault() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: vault.reset,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultKeys.snapshot });
      toast.success(t('toast.deleted'));
    },
  });
}
